import { Stack } from "../../../stack";
import { Text } from "../../../text";
import { InteractionIcon } from "../../../interaction-icon";
import { format, formatDistanceToNow } from "date-fns";
import { Skeleton } from "../../../skeleton";
import { ISidebarItemProps } from "./sidebar-content.types";
import { kebabCase } from "lodash";
import { SidebarItemWrapper } from "./sidebar-item-wrapper";

/**
 * Renders an individual sidebar interaction item with icon, formatted date, and time distance.
 * @param {InteractionType} props.interactionType - The type of interaction to display the correct icon.
 * @param {Date} props.dateTime - The date and time of the interaction.
 * @param {Function} props.onClick - Optional click handler when the item is selected.
 * @param {boolean} props.active - Optional flag indicating if this item is the currently viewed interaction.
 * @returns {JSX.Element} The Item component.
 */
const Item = ({ interactionType, dateTime, onClick, active }: ISidebarItemProps) => {
    return (
        <SidebarItemWrapper
            onClick={onClick}
            active={active}
            data-testid={`interaction-sidebar-item-${kebabCase(interactionType)}`}
        >
            <InteractionIcon size="md" type={interactionType} />
            <Stack gap="0" data-hide-on-sidebar-collapse>
                <Text noWrap size="sm" data-testid="interaction-sidebar-item-date">
                    {format(dateTime, "d LLL, h:mmaaa")}
                </Text>
                <Text noWrap variant="medium" size="xs" dimmed>
                    {formatDistanceToNow(dateTime)} ago
                </Text>
            </Stack>
        </SidebarItemWrapper>
    );
};

/**
 * Loading skeleton placeholder for the sidebar Item component.
 * @returns {JSX.Element} The ItemSkeleton component.
 */
const ItemSkeleton = () => <Skeleton height={44} />;
Item.Skeleton = ItemSkeleton;

export { Item };
